import { useState } from "react";
import { POST_PURPOSES, POST_STATUSES } from "@unik/shared";
import { BrandMark } from "../ui/BrandMark";
import { Button } from "../ui/Button";
import { PurposeBadge, StatusBadge } from "../ui/Badge";
import { Card, EmptyState, Skeleton } from "../ui/Card";
import { CaptionField, Input } from "../ui/Input";

/**
 * Prova visual do design system — docs/06 §10.
 * É o critério de aceite da Onda 10 e o lugar onde se detecta regressão visual.
 */
export function DesignSystem() {
  const [caption, setCaption] = useState("Toda peça tem uma função.");

  return (
    <div className="app-shell mx-auto max-w-3xl px-5 py-12">
      <header className="mb-12">
        <p className="type-overline text-gold">Genesis Lux</p>
        <h1 className="type-display mt-3 text-text-cream">Design System</h1>
        <p className="type-body mt-4 text-text-secondary">
          Todos os componentes, em todos os estados. Se algo aqui quebrar, quebrou no app.
        </p>
      </header>

      <Section title="Marca oficial">
        <p className="type-caption mb-2 text-text-tertiary">
          Ativos extraídos do kit oficial (Unik.ai, 26/jul) — logotipo principal, secundário e o
          símbolo, na versão creme (fundo escuro).
        </p>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col items-center gap-3 rounded-[var(--radius-control-lg)] bg-surface-raised p-6">
            <BrandMark variant="creme" className="h-8" />
            <p className="type-caption text-text-tertiary">Logotipo principal</p>
          </div>
          <div className="flex flex-col items-center gap-3 rounded-[var(--radius-control-lg)] bg-surface-raised p-6">
            <img src="/brand/logotipo-secundario-creme.png" alt="Selo Unik" className="h-24 w-auto" />
            <p className="type-caption text-text-tertiary">Logotipo secundário</p>
          </div>
          <div className="flex flex-col items-center gap-3 rounded-[var(--radius-control-lg)] bg-surface-raised p-6">
            <img src="/brand/simbolo-creme.png" alt="Símbolo Unik" className="h-20 w-auto" />
            <p className="type-caption text-text-tertiary">Símbolo</p>
          </div>
        </div>
      </Section>

      <Section title="Tipografia">
        <p className="type-display text-text-cream">Display — Bodoni Moda 600</p>
        <p className="type-h1 text-text-cream">Título 1</p>
        <p className="type-h2 text-text-cream">Título 2</p>
        <p className="type-h3">Título 3 — Montserrat 600</p>
        <p className="type-body text-text-primary">
          Corpo. Não vendo post bonito. Vendo crescimento real.
        </p>
        <p className="type-body-sm text-text-secondary">Corpo pequeno, apoio.</p>
        <p className="type-label text-text-secondary">Rótulo de campo</p>
        <p className="type-caption text-text-tertiary">Legenda, timestamp</p>
        <p className="type-overline text-gold">Overline — o token da marca</p>
      </Section>

      <Section title="Paleta">
        <p className="type-caption mb-2 text-text-tertiary">
          Vinho, carmim e creme são a base oficial e intocável; o resto é derivado por HSL.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Swatch name="vinho (oficial)" hex="#4C1C1A" />
          <Swatch name="carmim (oficial)" hex="#5F221D" />
          <Swatch name="creme (oficial)" hex="#E3DAC9" dark />
          <Swatch name="gold" hex="#C2A06B" dark />
          <Swatch name="surface-base" hex="#140808" />
          <Swatch name="surface-raised" hex="#24100F" />
          <Swatch name="accent" hex="#B3352B" />
          <Swatch name="text-cream" hex="#E3DAC9" dark />
          <Swatch name="success" hex="#3FA96B" dark />
          <Swatch name="warning" hex="#D9A441" dark />
          <Swatch name="danger" hex="#E8455F" />
          <Swatch name="info" hex="#7BA7D9" dark />
        </div>
      </Section>

      <Section title="Botões">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary">Primário</Button>
          <Button variant="secondary">Secundário</Button>
          <Button variant="ghost">Fantasma</Button>
          <Button variant="danger">Destrutivo</Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button size="sm">Pequeno</Button>
          <Button size="md">Médio</Button>
          <Button size="lg">Grande</Button>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button loading>Carregando</Button>
          <Button disabled>Desabilitado</Button>
        </div>
        <div className="pt-2">
          <p className="type-caption mb-3 text-text-tertiary">
            O botão mais importante do app — serifado, 56px, largura total no celular.
          </p>
          <Button size="lg" fullWidth>
            Aprovar
          </Button>
        </div>
      </Section>

      <Section title="Selos de estado — visão da Lara">
        <div className="flex flex-wrap gap-2">
          {POST_STATUSES.map((status) => (
            <StatusBadge key={status} status={status} audience="admin" />
          ))}
        </div>
      </Section>

      <Section title="Selos de estado — visão da cliente">
        <p className="type-caption mb-3 text-text-tertiary">
          A cliente nunca vê &ldquo;Falhou&rdquo; nem &ldquo;Publicando&rdquo;. Problema técnico é
          assunto entre o sistema e a Lara.
        </p>
        <div className="flex flex-wrap gap-2">
          {POST_STATUSES.map((status) => (
            <StatusBadge key={status} status={status} audience="client" />
          ))}
        </div>
      </Section>

      <Section title="Função do post — Método LL">
        <div className="flex flex-wrap gap-2">
          {POST_PURPOSES.map((purpose) => (
            <PurposeBadge key={purpose} purpose={purpose} />
          ))}
        </div>
      </Section>

      <Section title="Liquid Glass — a assinatura da marca">
        <p className="type-caption mb-4 text-text-tertiary">
          Portado do site dela: blur + saturate, brilho interno, borda que acende em carmim.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Card glass interactive>
            <h3 className="type-h3 text-text-cream">Vidro interativo</h3>
            <p className="type-body-sm mt-2 text-text-secondary">Passe o mouse — a borda acende.</p>
          </Card>
          <div className="liquid-glass-brand rounded-[var(--radius-invite)] p-5">
            <h3 className="type-h3 text-text-on-brand">Vidro de marca</h3>
            <p className="type-body-sm mt-2 text-text-on-brand/80">Preenchido em burgundy.</p>
          </div>
        </div>
        <div className="grain ambient-glow mt-4 rounded-[var(--radius-control-lg)] p-8 text-center">
          <p className="type-display text-brand-gradient">crescimento real</p>
          <p className="type-caption mt-2 text-text-tertiary">
            texto em gradiente + grão + brilho de ambiente
          </p>
        </div>
      </Section>

      <Section title="Cartões">
        <Card>
          <h3 className="type-h3 text-text-cream">Cartão padrão</h3>
          <p className="type-body-sm mt-2 text-text-secondary">
            Sombra com blur alto e opacidade baixa. Nunca sombra curta e dura.
          </p>
        </Card>
        <Card interactive>
          <h3 className="type-h3 text-text-cream">Cartão clicável</h3>
          <p className="type-body-sm mt-2 text-text-secondary">Passe o mouse.</p>
        </Card>
        <Card selected>
          <h3 className="type-h3 text-text-cream">Cartão selecionado</h3>
        </Card>
      </Section>

      <Section title="Campos">
        <Input label="Nome da clínica" placeholder="Clínica Aurora" />
        <Input label="E-mail" type="email" hint="Usado para avisos do sistema." />
        <Input label="WhatsApp" error="Informe um número válido." defaultValue="31 9" />
        <Input label="Desabilitado" disabled defaultValue="Não editável" />
        <CaptionField
          label="Legenda"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
      </Section>

      <Section title="Carregando">
        <Card>
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="mt-3 h-4 w-full" />
          <Skeleton className="mt-2 h-4 w-4/5" />
        </Card>
      </Section>

      <Section title="Estado vazio">
        <Card>
          <EmptyState
            title="Nenhum conteúdo na esteira."
            description="Assim que os posts do mês forem enviados, eles aparecem aqui."
            action={<Button>Subir conteúdo</Button>}
          />
        </Card>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-12 border-t border-[var(--border-subtle)] pt-8">
      <h2 className="type-overline mb-6 text-text-tertiary">{title}</h2>
      <div className="flex flex-col gap-6">{children}</div>
    </section>
  );
}

function Swatch({ name, hex, dark = false }: { name: string; hex: string; dark?: boolean }) {
  return (
    <div className="overflow-hidden rounded-sm border border-[var(--border-subtle)]">
      <div className="h-16" style={{ background: hex }} />
      <div className="bg-surface-raised px-3 py-2">
        <p className="type-caption text-text-secondary">{name}</p>
        <p className={`type-caption tabular-nums ${dark ? "text-text-tertiary" : "text-text-tertiary"}`}>
          {hex}
        </p>
      </div>
    </div>
  );
}
